import * as React from 'react';
import { useState, useMemo, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { EventType, UserPreference } from './index';
import { useIsMobile } from '../../../hooks/useIsMobile';

interface Props {
    eventTypes: EventType[];
    preferences: UserPreference[];
    onSave: (
        eventTypeId: number | null,
        channel: string | null,
        body: Partial<Pick<UserPreference, 'enabled' | 'quiet_hours_start' | 'quiet_hours_end' | 'timezone' | 'dnd_until'>>,
    ) => void | Promise<void>;
}

// Channels exposed in the UI. Email is intentionally NOT in the UI — Kosovo
// manufacturing context doesn't lean on email; including it would over-promise.
// The backend still accepts `channel: 'email'` in validation so future tenants
// in markets that DO use email can be opted in by toggling a row — just add
// the entry here.
const CHANNEL_COLUMNS: { key: string; labelId: string; defaultLabel: string; disabled: boolean }[] = [
    { key: 'inapp',   labelId: 'notifications.settings.types.inApp',  defaultLabel: 'In-app',       disabled: false },
    { key: 'webpush', labelId: 'notifications.settings.types.push',   defaultLabel: 'Browser push', disabled: false },
];

/**
 * Module ordering — mirrors the dashboard nav so users see the same mental
 * sectioning ("Sales / Production / After-sales") they're used to. Unknown
 * modules fall through to the end alphabetically.
 */
const MODULE_ORDER = [
    'contact', 'measure', 'offer', 'agreement',
    'production',
    'warehouse', 'transport', 'installation', 'servicing',
    'admin', 'system',
];

/**
 * Quick-apply role presets. Each preset is a SET of event keys that should be
 * enabled across ALL channels — everything else is implicitly disabled. The
 * "Office" preset for example would silence production noise for a sales rep
 * who only cares about offers + clients.
 *
 * To add a role: drop a new entry here. To change which events a role gets,
 * edit the `keys` array. No backend changes needed — applying a preset just
 * fires a batch of PUT /preferences calls.
 */
interface Preset {
    key: string;
    labelId: string;
    defaultLabel: string;
    emoji: string;
    keys: string[];
}

const PRESETS: Preset[] = [
    {
        key: 'magazinier',
        labelId: 'notifications.settings.presets.magazinier',
        defaultLabel: 'Magazinier',
        emoji: '🏭',
        keys: [
            'warehouse.low_stock',
            'warehouse.received',
            'production.process.completed',
            'production.order.completed',
            'transport.assigned',
        ],
    },
    {
        key: 'production_worker',
        labelId: 'notifications.settings.presets.productionWorker',
        defaultLabel: 'Punëtor prodhimi',
        emoji: '🛠',
        keys: [
            'production.process.completed',
            'production.process.blocked',
            'production.process.returned',
            'production.comment.mentioned',
        ],
    },
    {
        key: 'production_manager',
        labelId: 'notifications.settings.presets.productionManager',
        defaultLabel: 'Menaxher prodhimi',
        emoji: '👷',
        keys: [
            'production.process.completed',
            'production.process.blocked',
            'production.process.returned',
            'production.order.completed',
            'production.comment.created',
            'production.comment.mentioned',
            'measure.completed',
            'offer.accepted',
            'transport.assigned',
            'warehouse.low_stock',
        ],
    },
    {
        key: 'installer',
        labelId: 'notifications.settings.presets.installer',
        defaultLabel: 'Instalues',
        emoji: '🔧',
        keys: [
            'installation.assigned',
            'installation.blocked',
            'installation.completed',
            'installation.partial',
            'transport.completed',
        ],
    },
    {
        key: 'office',
        labelId: 'notifications.settings.presets.office',
        defaultLabel: 'Office',
        emoji: '💼',
        keys: [
            'project.created',
            'offer.accepted',
            'offer.rejected',
            'offer.expiring_soon',
            'agreement.created',
            'agreement.signed',
            'servicing.created',
        ],
    },
];

/**
 * Convert a key like "production.process.completed" into a readable fallback
 * for events whose translation isn't shipped yet — "Production process completed".
 */
function humanize(key: string): string {
    const last = key.split('.').slice(1).join(' ');
    if (!last) return key;
    return last.charAt(0).toUpperCase() + last.slice(1).replace(/_/g, ' ');
}

/**
 * Render a chunked progress bar like ▰▰▰▱▱▱ — N filled segments out of total.
 */
const ProgressBar: React.FC<{ value: number; total: number }> = ({ value, total }) => {
    const filled = Math.max(0, Math.min(total, value));
    return (
        <span className="notif-settings__progress" aria-hidden="true">
            {Array.from({ length: total }).map((_, i) => (
                <span
                    key={i}
                    className={`notif-settings__progress-cell ${i < filled ? 'is-filled' : ''}`}
                />
            ))}
        </span>
    );
};

const EventTogglesSection: React.FC<Props> = ({ eventTypes, preferences, onSave }) => {
    const intl = useIntl();
    const isMobile = useIsMobile();
    // Start collapsed — let the user open what they care about. Counts in the
    // section headers are enough preview.
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    // Live preview is collapsed by default on mobile (200px is precious on a
    // ~600px-tall mobile viewport), expanded by default on desktop where the
    // example is genuinely helpful while configuring.
    const [previewOpen, setPreviewOpen] = useState<boolean>(!isMobile);

    // Pre-compute "what's the current enabled state for (event, channel)?"
    // Dispatcher default = enabled unless an explicit row says otherwise.
    const getEnabled = useCallback((eventTypeId: number, channel: string): boolean => {
        const specific = preferences.find((p) =>
            p.event_type_id === eventTypeId && p.channel === channel,
        );
        if (specific) return specific.enabled;
        return true;
    }, [preferences]);

    // Group event types by module for collapsible sections.
    const grouped = useMemo(() => {
        const map = new Map<string, EventType[]>();
        for (const et of eventTypes) {
            const mod = et.module || 'other';
            if (!map.has(mod)) map.set(mod, []);
            map.get(mod)!.push(et);
        }
        const ordered: Array<[string, EventType[]]> = [];
        for (const mod of MODULE_ORDER) {
            if (map.has(mod)) {
                ordered.push([mod, map.get(mod)!]);
                map.delete(mod);
            }
        }
        for (const mod of Array.from(map.keys()).sort()) {
            ordered.push([mod, map.get(mod)!]);
        }
        return ordered;
    }, [eventTypes]);

    // Total enabled / disabled across all events + channels — drives the
    // section header pill.
    const stats = useMemo(() => {
        let enabled = 0;
        let disabled = 0;
        for (const et of eventTypes) {
            for (const col of CHANNEL_COLUMNS) {
                if (getEnabled(et.id, col.key)) enabled++;
                else disabled++;
            }
        }
        return { enabled, disabled, total: eventTypes.length * CHANNEL_COLUMNS.length };
    }, [eventTypes, getEnabled]);

    // Per-module enabled count for the progress bar. We count events where at
    // least ONE channel is enabled (otherwise an "all push off but all in-app
    // on" row would read as half-enabled which is confusing).
    const moduleStats = useMemo(() => {
        const stats: Record<string, { enabled: number; total: number }> = {};
        for (const [module, events] of grouped) {
            let on = 0;
            for (const et of events) {
                if (CHANNEL_COLUMNS.some((col) => getEnabled(et.id, col.key))) on++;
            }
            stats[module] = { enabled: on, total: events.length };
        }
        return stats;
    }, [grouped, getEnabled]);

    const toggleModule = (mod: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(mod)) next.delete(mod);
            else next.add(mod);
            return next;
        });
    };

    const handleToggle = (eventTypeId: number, channel: string, nextEnabled: boolean) => {
        onSave(eventTypeId, channel, { enabled: nextEnabled });
    };

    const applyPreset = useCallback((preset: Preset) => {
        // For each event type: enable if it's in the preset, else disable.
        // We send one PUT per (event, channel) — small batch, fires in parallel
        // via the parent's upsert helper.
        for (const et of eventTypes) {
            const shouldEnable = preset.keys.includes(et.key);
            for (const col of CHANNEL_COLUMNS) {
                const current = getEnabled(et.id, col.key);
                if (current !== shouldEnable) {
                    onSave(et.id, col.key, { enabled: shouldEnable });
                }
            }
        }
    }, [eventTypes, getEnabled, onSave]);

    return (
        <section className="notif-settings__section notif-settings__section--types">
            <header className="notif-settings__section-head">
                <span className="notif-settings__section-icon" aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="4" y1="6" x2="20" y2="6" />
                        <line x1="4" y1="12" x2="20" y2="12" />
                        <line x1="4" y1="18" x2="14" y2="18" />
                        <circle cx="9" cy="6" r="2" fill="currentColor" stroke="none" />
                        <circle cx="16" cy="12" r="2" fill="currentColor" stroke="none" />
                        <circle cx="7" cy="18" r="2" fill="currentColor" stroke="none" />
                    </svg>
                </span>
                <div className="notif-settings__section-head-text">
                    <h2 className="notif-settings__section-title">
                        <FormattedMessage id="notifications.settings.types.title" defaultMessage="What you get notified about" />
                    </h2>
                    <p className="notif-settings__section-desc">
                        <FormattedMessage
                            id="notifications.settings.types.desc"
                            defaultMessage="Choose exactly which events you receive and where. Toggle off to mute an event for that channel."
                        />
                    </p>
                </div>
                <span className="notif-settings__pill notif-settings__pill--types-active">
                    <FormattedMessage
                        id="notifications.settings.types.pillCount"
                        defaultMessage="{enabled} AKTIVE · {disabled} HESHTUR"
                        values={{ enabled: stats.enabled, disabled: stats.disabled }}
                    />
                </span>
            </header>

            {/* Quick presets — one-click role-based bulk config. */}
            <div className="notif-settings__presets">
                <span className="notif-settings__presets-label">
                    <FormattedMessage id="notifications.settings.presets.label" defaultMessage="Apliko preset:" />
                </span>
                <div className="notif-settings__presets-chips">
                    {PRESETS.map((preset) => (
                        <button
                            key={preset.key}
                            type="button"
                            className="notif-settings__preset-chip"
                            onClick={() => applyPreset(preset)}
                        >
                            <span aria-hidden="true">{preset.emoji}</span>
                            <FormattedMessage id={preset.labelId} defaultMessage={preset.defaultLabel} />
                        </button>
                    ))}
                </div>
            </div>

            <div className="notif-settings__channel-legend">
                <span className="notif-settings__channel-legend-cell">&nbsp;</span>
                {CHANNEL_COLUMNS.map((col) => (
                    <span
                        key={col.key}
                        className={`notif-settings__channel-legend-cell ${col.disabled ? 'is-disabled' : ''}`}
                    >
                        <FormattedMessage id={col.labelId} defaultMessage={col.defaultLabel} />
                        {col.disabled && (
                            <small className="notif-settings__channel-soon">
                                <FormattedMessage id="notifications.settings.types.comingSoon" defaultMessage="Soon" />
                            </small>
                        )}
                    </span>
                ))}
            </div>

            <div className="notif-settings__modules">
                {grouped.map(([module, events]) => {
                    const isOpen = expanded.has(module);
                    const ms = moduleStats[module] || { enabled: 0, total: events.length };
                    return (
                        <div key={module} className={`notif-settings__module ${isOpen ? 'is-open' : ''}`}>
                            <button
                                type="button"
                                className="notif-settings__module-head"
                                onClick={() => toggleModule(module)}
                                aria-expanded={isOpen}
                            >
                                <span className="notif-settings__module-chevron" aria-hidden="true">
                                    {isOpen ? '▾' : '▸'}
                                </span>
                                <span className="notif-settings__module-name">
                                    <FormattedMessage
                                        id={`notifications.category.${module}`}
                                        defaultMessage={module.charAt(0).toUpperCase() + module.slice(1)}
                                    />
                                </span>
                                <ProgressBar value={ms.enabled} total={ms.total} />
                                <span className="notif-settings__module-count">
                                    {ms.enabled}/{ms.total}
                                </span>
                            </button>

                            {isOpen && (
                                <div className="notif-settings__events">
                                    {events.map((event) => (
                                        <div key={event.id} className="notif-settings__event-row">
                                            <span className="notif-settings__event-label">
                                                <FormattedMessage
                                                    id={event.label_key}
                                                    defaultMessage={humanize(event.key)}
                                                />
                                            </span>
                                            {CHANNEL_COLUMNS.map((col) => {
                                                const enabled = getEnabled(event.id, col.key);
                                                return (
                                                    <label
                                                        key={col.key}
                                                        className={`notif-settings__channel-toggle ${col.disabled ? 'is-disabled' : ''}`}
                                                        aria-label={`${event.key} — ${col.key}`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={enabled}
                                                            disabled={col.disabled}
                                                            onChange={(e) => handleToggle(event.id, col.key, e.target.checked)}
                                                        />
                                                        <span className="notif-settings__channel-toggle-slider" />
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Live preview — concrete example of what one of these notifications
                actually looks like. Hardcoded sample keeps the demo predictable
                regardless of the user's history.

                Collapsed by default on mobile (saves ~200px of vertical space
                on a phone), expanded by default on desktop where there's room. */}
            <div className={`notif-settings__preview ${previewOpen ? 'is-open' : 'is-closed'}`}>
                <button
                    type="button"
                    className="notif-settings__preview-label"
                    onClick={() => setPreviewOpen((o) => !o)}
                    aria-expanded={previewOpen}
                >
                    <FormattedMessage id="notifications.settings.types.previewLabel" defaultMessage="SHEMBULL I NJOFTIMIT" />
                    <svg
                        className="notif-settings__preview-chevron"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </button>
                {previewOpen && (
                <div className="notif-settings__preview-card">
                    <div className="notif-settings__preview-icon" aria-hidden="true">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
                            <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
                        </svg>
                    </div>
                    <div className="notif-settings__preview-body">
                        <div className="notif-settings__preview-title">
                            <FormattedMessage
                                id="notifications.settings.types.previewTitle"
                                defaultMessage="Procesi i prodhimit u përfundua"
                            />
                        </div>
                        <div className="notif-settings__preview-text">
                            <FormattedMessage
                                id="notifications.settings.types.previewBody"
                                defaultMessage="Prerja sharre për Enis Kameri / Shkallet me trar u përfundua."
                            />
                        </div>
                        <div className="notif-settings__preview-meta">
                            <FormattedMessage
                                id="notifications.settings.types.previewMeta"
                                defaultMessage="Para 12 min · Brenda app + Shfletues"
                            />
                        </div>
                    </div>
                </div>
                )}
            </div>
        </section>
    );
};

export default EventTogglesSection;
